import { TagIcon } from "icons/TagIcon";
import { WorkspaceIcon } from "icons/WorkspaceIcon";
import { ClipboardIcon } from "icons/ClipboardIcon";
import { LightbulbIcon } from "icons/LightbulbIcon";
import { TemplateIcon } from "icons/TemplateIcon";
import { FolderIcon } from "icons/FolderIcon";
import { DocumentIcon } from "icons/DocumentIcon";
import { UsersIcon } from "icons/UsersIcon";
import { MonitorIcon } from "icons/MonitorIcon";
import { ChartBarIcon } from "icons/ChartBarIcon";
import { ConversationIcon } from "icons/ConversationIcon";
import { MegaphoneIcon } from "icons/MegaphoneIcon";
import type {
  IAdminSidebarCategory,
  IAdminSidebarItem,
  AdminSidebarRole,
} from "./adminSidebar.types";

export const ADMIN_SIDEBAR_CONFIG: IAdminSidebarCategory[] = [
  {
    name: "CONTENT",
    items: [
      {
        key: "tags",
        label: "Knowledge Components",
        icon: TagIcon,
        requiredRole: "admin",
        hasBadge: true,
        slug: "knowledge-components",
      },
      {
        key: "categories",
        label: "Knowledge Components Categories",
        icon: WorkspaceIcon,
        requiredRole: "admin",
        hasBadge: true,
        slug: "categories",
      },
      {
        key: "useCases",
        label: "Use Cases",
        icon: ClipboardIcon,
        requiredRole: "admin",
        hasBadge: true,
        slug: "use-cases",
      },
      {
        key: "useCasePrompts",
        label: "Use Case Prompts",
        icon: LightbulbIcon,
        requiredRole: "admin",
        hasBadge: true,
        slug: "use-case-prompts",
      },
      {
        key: "workspaces",
        label: "Workspaces",
        icon: FolderIcon,
        requiredRole: "admin",
        hasBadge: true,
        slug: "workspaces",
      },
      {
        key: "templates",
        label: "Quick Start Templates",
        icon: TemplateIcon,
        requiredRole: "admin",
        hasBadge: true,
        slug: "templates",
      },
      {
        key: "helpPageDocuments",
        label: "Help Page Documents",
        icon: DocumentIcon,
        requiredRole: "platformAdmin",
        hasBadge: true,
        slug: "help-page-documents",
      },
    ],
  },
  {
    name: "USERS & ACCESS",
    items: [
      {
        key: "users",
        label: "Elevated Users",
        icon: UsersIcon,
        requiredRole: "platformAdmin",
        hasBadge: true,
        slug: "elevated-users",
      },
      {
        key: "aiAccess",
        label: "AI Models Access",
        icon: MonitorIcon,
        requiredRole: "platformAdmin",
        hasBadge: true,
        slug: "ai-models-access",
      },
    ],
  },
  {
    name: "ANALYTICS",
    items: [
      {
        key: "statistics",
        label: "Statistics",
        icon: ChartBarIcon,
        requiredRole: "admin",
        hasBadge: false,
        slug: "statistics",
      },
      {
        key: "interviewsStatistics",
        label: "Statistics - AI Interviews",
        icon: ChartBarIcon,
        requiredRole: "admin",
        hasBadge: false,
        slug: "ai-interviews-statistics",
      },
      {
        key: "feedback",
        label: "User Feedback",
        icon: ConversationIcon,
        requiredRole: "admin",
        hasBadge: false,
        slug: "user-feedback",
      },
    ],
  },
  {
    name: "SYSTEM",
    items: [
      {
        key: "announcements",
        label: "Announcements",
        icon: MegaphoneIcon,
        requiredRole: "platformAdmin",
        hasBadge: true,
        slug: "announcements",
      },
    ],
  },
];

export const getVisibleItems = (
  userRole: AdminSidebarRole,
): IAdminSidebarCategory[] => {
  return ADMIN_SIDEBAR_CONFIG.map((category) => ({
    ...category,
    items: category.items.filter((item) => {
      if (userRole === "platformAdmin") return true;
      return item.requiredRole === "admin";
    }),
  })).filter((category) => category.items.length > 0);
};

export const getAllSectionKeys = (): string[] => {
  return ADMIN_SIDEBAR_CONFIG.flatMap((category) =>
    category.items.map((item) => item.key),
  );
};

export const findSidebarItem = (key: string): IAdminSidebarItem | undefined => {
  for (const category of ADMIN_SIDEBAR_CONFIG) {
    const item = category.items.find((item) => item.key === key);
    if (item) return item;
  }
  return undefined;
};

export const findItemBySlug = (
  slug: string,
  userRole: AdminSidebarRole,
): IAdminSidebarItem | undefined => {
  const visibleCategories = getVisibleItems(userRole);
  for (const category of visibleCategories) {
    const item = category.items.find((item) => item.slug === slug);
    if (item) return item;
  }
  return undefined;
};

export const getSlugForKey = (key: string): string | undefined => {
  const item = findSidebarItem(key);
  return item?.slug;
};
